import React from 'react';
import { useSelector } from 'react-redux';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import Tooltip from '@mui/material/Tooltip';
import CustomTable from '../table/table';
import './sync-contacts-modal.scss';

export default function SyncContactsTable({
    entityType,
    activeModalTab,
    setActiveModalTab,
    selectedIds,
    modalCustomers,
    modalSuppliers,
    modalContractors,
    isAllSelectedForCurrentTab,
    toggleSelectAllForCurrentTab,
    toggleRowSelection,
    getActiveContactsForTab,
    syncLogs = {},
    syncing = false
}) {
    const user = useSelector((state) => state.user.userData);
    const permissions = user?.permissions || {};
    const hasCustomerWrite = Boolean(user?.global_user || permissions?.customer === 2);
    const hasSupplierWrite = Boolean(user?.global_user || permissions?.supplier === 2);
    const hasContractorWrite = Boolean(user?.global_user || permissions?.contractor === 2);
    const modalCustomerColumns = [
        {
            title: (
                <Form.Check
                    type="checkbox"
                    checked={isAllSelectedForCurrentTab()}
                    onChange={() => toggleSelectAllForCurrentTab()}
                    disabled={syncing}
                />
            ),
            field: "select",
            class: "w-50px",
            render: (row) => (
                <Form.Check
                    type="checkbox"
                    checked={selectedIds.includes(row.id)}
                    onChange={() => toggleRowSelection(row.id)}
                    disabled={syncing}
                />
            )
        },
        { title: "Customer Number", field: "customerNumber" },
        { title: "Company Name", field: "businessName" },
        { title: "Address", field: "address" },
        { title: "Phone Number", field: "phoneNumber" },
        { title: "Contact", field: "contacts" },
        { title: "Email", field: "email" },
        { title: "Type", field: "type" }
    ];

    const modalVendorColumns = [
        {
            title: (
                <Form.Check
                    type="checkbox"
                    checked={isAllSelectedForCurrentTab()}
                    onChange={() => toggleSelectAllForCurrentTab()}
                    disabled={syncing}
                />
            ),
            field: "select",
            class: "w-50px",
            render: (row) => (
                <Form.Check
                    type="checkbox"
                    checked={selectedIds.includes(row.id)}
                    onChange={() => toggleRowSelection(row.id)}
                    disabled={syncing}
                />
            )
        },
        { title: "Vendor Number", field: "vendorNumber" },
        { title: "Company Name", field: "businessName" },
        { title: "Address", field: "address" },
        { title: "Phone Number", field: "phoneNumber" },
        { title: "Contact", field: "contacts" },
        { title: "Email", field: "email" },
        { 
            title: "Active", 
            field: "isActive",
            render: (row) => row.isActive ? "Active" : "Inactive"
        }
    ];

    const pushStatusColumn = {
        title: "Push Status",
        field: "pushStatus",
        class: "text-center align-middle w-120px",
        render: (row) => {
            const log = syncLogs[row.id];
            if (!log) return null;
            return (
                <>
                    {log.status === 'pending' && (
                        <Tooltip
                            title="Queued"
                            placement="top"
                            arrow
                            followCursor={true}
                            PopperProps={{ style: { zIndex: 99999 } }}
                        >
                            <span>
                                <HourglassEmptyIcon className="sync-status-icon sync-status-icon--pending" style={{ fontSize: '0.875rem', width: '0.875rem', height: '0.875rem', color: '#6c757d' }} />
                            </span>
                        </Tooltip>
                    )}
                    {log.status === 'syncing' && (
                        <Tooltip
                            title="Pushing to accounting platform..."
                            placement="top"
                            arrow
                            followCursor={true}
                            PopperProps={{ style: { zIndex: 99999 } }}
                        >
                            <span>
                                <Spinner animation="border" size="sm" className="text-primary sync-spinner-sm" style={{ width: '0.875rem', height: '0.875rem' }} />
                            </span>
                        </Tooltip>
                    )}
                    {log.status === 'success' && (
                        <Tooltip
                            title="Synced Successfully"
                            placement="top"
                            arrow
                            followCursor={true}
                            PopperProps={{ style: { zIndex: 99999 } }}
                        >
                            <span>
                                <CheckCircleIcon className="sync-status-icon sync-status-icon--success" style={{ fontSize: '0.875rem', width: '0.875rem', height: '0.875rem', color: '#198754' }} />
                            </span>
                        </Tooltip>
                    )}
                    {log.status === 'error' && (
                        <Tooltip
                            title={<div className="sync-error-tooltip-content">{log.msg}</div>}
                            placement="top"
                            arrow
                            followCursor={true}
                            PopperProps={{
                                style: { zIndex: 99999 }
                            }}
                        >
                            <span className="d-inline-flex align-items-center sync-error-link text-danger">
                                <CancelIcon className="sync-status-icon sync-status-icon--error" style={{ fontSize: '0.875rem', width: '0.875rem', height: '0.875rem', color: 'inherit', verticalAlign: 'middle' }} />
                                <span className="ms-1 small fw-semibold" style={{ color: 'inherit' }}>View Error</span>
                            </span>
                        </Tooltip>
                    )}
                </>
            );
        }
    };

    if (Object.keys(syncLogs).length > 0) {
        modalCustomerColumns.push(pushStatusColumn);
        modalVendorColumns.push(pushStatusColumn);
    }

    const renderTable = (columns, data, key) => (
        <CustomTable
            key={key}
            columns={columns}
            data={data}
            toolbar={true}
            itemsPerPageCustom={false}
            itemsPerPage={10}
            exportButton={false}
            addButton={false}
            isEdit={false}
            isDelete={false}
            isMore={false}
            additionalActions={[]}
            fileName={`modal_sync_${key}`}
            isLoading={syncing}
        />
    );

    return (
        <div className="scroll-container">
            {entityType === 'all' ? (
                <Tabs
                    activeKey={activeModalTab}
                    onSelect={(k) => {
                        if (!syncing) {
                            setActiveModalTab(k);
                        }
                    }}
                    className="mb-3"
                >
                    {hasCustomerWrite && (
                        <Tab eventKey="customer" title={`Customers (${modalCustomers.length})`} data-cy="sync-contacts-tab-customer">
                            {renderTable(modalCustomerColumns, modalCustomers, 'customer')}
                        </Tab>
                    )}
                    {hasSupplierWrite && (
                        <Tab eventKey="supplier" title={`Suppliers (${modalSuppliers.length})`} data-cy="sync-contacts-tab-supplier">
                            {renderTable(modalVendorColumns, modalSuppliers, 'supplier')}
                        </Tab>
                    )}
                    {hasContractorWrite && (
                        <Tab eventKey="contractor" title={`Contractors (${modalContractors.length})`} data-cy="sync-contacts-tab-contractor">
                            {renderTable(modalVendorColumns, modalContractors, 'contractor')}
                        </Tab>
                    )}
                </Tabs>
            ) : (
                renderTable(
                    entityType === 'customer' ? modalCustomerColumns : modalVendorColumns,
                    getActiveContactsForTab(),
                    entityType
                )
            )}
        </div>
    );
}
